import React, { useMemo, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { observer } from "mobx-react";
import { Spin, Button, Progress, Tooltip } from "antd";
import {
    ExclamationCircleOutlined,
    LoadingOutlined,
    CopyOutlined,
    LikeOutlined,
    LikeFilled,
    DislikeOutlined,
    DislikeFilled,
    CloseOutlined,
    EditOutlined,
} from "@ant-design/icons";
import Avatar from "@app/components/user/avatar";
import Loader from "@app/components/loader/content";
import format from "@app/lib/format";
import md from "@app/lib/md";
import chatStore from "../../state/store/ai-chat";
import { calculateAreaCoordinates, referenceLabel, stripRefMarkers } from "./helper";

import copilotLogo from "@app/assets/logo-icon2.svg";
import "./style/conversation.scoped.scss";
import { assembleMixedCopyContent } from "../pdf-viewer/copy-helper";

const SpinIcon = <LoadingOutlined style={{ fontSize: 18, marginRight: 10 }} spin />;

// Polling interval in milliseconds
const POLL_INTERVAL = 10000;

const escapeAttribute = (value) =>
    String(value).replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");

const History = observer(
    ({
        activeChat,
        loading,
        messages = [],
        onShowRef,
        onThumbsUp,
        onThumbsDown,
        onCancel,
        onEditMessage,
    }) => {
        const bottomRef = useRef(null);
        const lastMessage = useMemo(() => {
            return messages[messages.length - 1];
        }, [messages]);
        const allReferences = useMemo(() => {
            const refs = [];
            messages.forEach((msg) => {
                if (msg?.answer?.references) {
                    refs.push(...msg.answer.references);
                }
            });
            return refs;
        }, [messages]);

        useEffect(() => {
            chatStore.loadSourceDocs();
        }, []);

        // scroll to the bottom of the chat - immediate scroll when changing chats
        useEffect(() => {
            if (bottomRef?.current) {
                setTimeout(() => {
                    bottomRef.current.scrollIntoView();
                }, 0);
            }
        }, [activeChat, messages.length, bottomRef?.current]);

        // scroll to the bottom of the chat
        useEffect(() => {
            if (bottomRef?.current) {
                setTimeout(() => {
                    bottomRef.current.scrollIntoView({ behavior: "smooth" });
                }, 0);
            }
        }, [lastMessage?.status]);

        return (
            <div className="history">
                {loading && (
                    <div className="loading">
                        <Loader loading={true} />
                    </div>
                )}

                {!loading && (
                    <>
                        {!activeChat && (
                            <div className="help">
                                Please type your question below and hit Enter <br />
                                to start your conversation with CAPTIS Copilot.
                            </div>
                        )}

                        {messages.map((record, index) => {
                            const isLastMessage = index === messages.length - 1;
                            return (
                                <HistoryRecord
                                    chatId={activeChat}
                                    record={record}
                                    allReferences={allReferences}
                                    key={record._id}
                                    onShowRef={onShowRef}
                                    onThumbsUp={() => onThumbsUp(record._id)}
                                    onThumbsDown={() => onThumbsDown(record._id)}
                                    onCancel={isLastMessage ? onCancel : undefined}
                                    onEditMessage={onEditMessage}
                                />
                            );
                        })}
                    </>
                )}

                <div ref={bottomRef}></div>
            </div>
        );
    },
);

const CopilotLogo = observer(({ loading, size = 48 }) => {
    return (
        <div className="copilot-logo" style={{ width: size, height: size }}>
            <img src={copilotLogo} />
            {loading && (
                <div className="loading">
                    <Spin indicator={SpinIcon} />
                </div>
            )}
        </div>
    );
});

const Operation = observer(({ children, progressPercentage, onCancel }) => {
    const hasProgress = typeof progressPercentage === "number";

    return (
        <div className="operation box">
            <div className="logo">
                <CopilotLogo loading size={28} />
            </div>
            <div className="operation-content">
                <div className="text">{children}</div>
                {hasProgress && (
                    <div className="progress-container">
                        <Progress
                            percent={progressPercentage}
                            size="small"
                            status="active"
                            strokeColor="#87d068"
                        />
                    </div>
                )}
            </div>
            {onCancel && (
                <Button
                    type="text"
                    icon={<CloseOutlined />}
                    onClick={onCancel}
                    className="cancel-btn"
                    title="Cancel request"
                />
            )}
        </div>
    );
});

const Error = observer(({ message }) => {
    return (
        <div className="error box">
            <ExclamationCircleOutlined className="icon" />
            <div className="text">
                {message ||
                    "An error occurred while preparing your response. Please try again or contact support for assistance."}
            </div>
        </div>
    );
});

const MAX_SKIPPED_NAMES = 5;

const skippedName = (entry) => {
    if (entry.mdrId) {
        return `C${entry.mdrId} - ${entry.name}`;
    }

    return entry.name ?? "Unnamed file";
};

const SkippedFiles = observer(({ skipped }) => {
    const names = skipped.map(skippedName);
    const shown = names.slice(0, MAX_SKIPPED_NAMES);
    const remaining = names.length - shown.length;

    return (
        <div className="skipped">
            <ExclamationCircleOutlined className="icon" />
            <div className="text">
                {skipped.length === 1
                    ? "1 selected file could not be processed and was not used for this answer: "
                    : `${skipped.length} selected files could not be processed and were not used for this answer: `}
                {shown.join("; ")}
                {remaining > 0 && ` and ${remaining} more`}.
            </div>
        </div>
    );
});

const Answer = observer(({ answer, allReferences, onShowRef, onThumbsUp, onThumbsDown }) => {
    const { project } = useParams();
    const contentRef = useRef(null);

    const lookupReferences =
        allReferences && allReferences.length > 0 ? allReferences : answer?.references || [];

    const content = (() => {
        let html = "";
        if (answer?.text) {
            html = md.toHtml(answer.text);

            if (lookupReferences.length > 0) {
                const chunkIdToIndex = new Map();
                lookupReferences.forEach((ref, index) => {
                    if (ref?.chunkId && !chunkIdToIndex.has(ref.chunkId)) {
                        chunkIdToIndex.set(ref.chunkId, index);
                    }
                });

                let counter = 0;
                html = html.replace(/\[:ref:([^\]]+):\]/g, (_match, chunkId) => {
                    const index = chunkIdToIndex.get(chunkId);
                    if (index === undefined) {
                        return "";
                    }

                    const ref = lookupReferences[index];
                    const refId = `ref-${index}`;
                    const label = escapeAttribute(referenceLabel(ref));
                    counter += 1;

                    return `<br/><a href="#" class="reference-link" data-ref-id="${refId}" data-tooltip="${label}">${counter}</a>`;
                });

                html = html.replace(/<\/a>\s*<br\/><a(?=[^>]*class="reference-link")/g, "</a><a");
            }
        }

        return html;
    })();

    // Set up click handler for reference links
    useEffect(() => {
        const handleReferenceClick = (e) => {
            if (e.target.classList.contains("reference-link")) {
                e.preventDefault();
                const refId = e.target.getAttribute("data-ref-id");
                if (refId) {
                    const index = parseInt(refId.replace("ref-", ""));
                    if (lookupReferences[index]) {
                        onShowRef(lookupReferences[index]);
                    }
                }
            }
        };

        const contentElement = contentRef.current;
        if (contentElement) {
            contentElement.addEventListener("click", handleReferenceClick);
        }

        return () => {
            if (contentElement) {
                contentElement.removeEventListener("click", handleReferenceClick);
            }
        };
    }, [lookupReferences, onShowRef]);

    function copy() {
        const data = {};
        data["text/plain"] = new Blob([stripRefMarkers(answer.text)], { type: "text/plain" });

        let htmlContent;
        if (lookupReferences.length > 0) {
            const parser = new DOMParser();
            const doc = parser.parseFromString(content, "text/html");

            const referenceLinks = Array.from(doc.querySelectorAll("a.reference-link"));

            const parentElements = new Set();
            referenceLinks.forEach((link) => {
                parentElements.add(link.parentElement);
            });

            if (parentElements.size === 1 && referenceLinks.length > 1) {
                const parent = Array.from(parentElements)[0];
                const newParent = doc.createElement("div");

                const segments = [];
                let currentSegment = { nodes: [], refIndex: null };

                const processNode = (node) => {
                    if (
                        node.nodeType === Node.ELEMENT_NODE &&
                        node.classList.contains("reference-link")
                    ) {
                        const refId = node.getAttribute("data-ref-id");
                        const refIndex = parseInt(refId.replace("ref-", ""));

                        const lastNode = currentSegment.nodes[currentSegment.nodes.length - 1];
                        if (
                            lastNode &&
                            lastNode.classList &&
                            lastNode.classList.contains("reference-link")
                        ) {
                            return;
                        }

                        if (currentSegment.nodes.length > 0) {
                            currentSegment.refIndex = refIndex;
                            segments.push(currentSegment);
                        }

                        currentSegment = { nodes: [], refIndex: null };
                    } else if (node.nodeName === "BR") {
                        const nextSibling = node.nextSibling;
                        if (
                            nextSibling &&
                            nextSibling.classList &&
                            nextSibling.classList.contains("reference-link")
                        ) {
                            return;
                        }
                        currentSegment.nodes.push(node.cloneNode(true));
                    } else {
                        if (node.nodeType === Node.TEXT_NODE && !node.textContent.trim()) {
                            return;
                        }
                        currentSegment.nodes.push(node.cloneNode(true));
                    }
                };

                Array.from(parent.childNodes).forEach(processNode);

                if (currentSegment.nodes.length > 0) {
                    segments.push(currentSegment);
                }

                segments.forEach((segment) => {
                    if (segment.nodes.length > 0) {
                        const p = doc.createElement("p");
                        segment.nodes.forEach((node) => {
                            p.appendChild(node);
                        });

                        if (segment.refIndex !== null) {
                            p.setAttribute("data-ref-mapping", segment.refIndex.toString());
                        }

                        newParent.appendChild(p);
                    }
                });

                const segmentNodes = Array.from(newParent.children);

                parent.replaceWith(segmentNodes[0] || parent);

                segmentNodes.slice(1).forEach((child) => {
                    parent.parentElement?.appendChild(child) || doc.body.appendChild(child);
                });
            } else {
                const elementToReferenceMap = new Map();

                referenceLinks.forEach((link) => {
                    const refId = link.getAttribute("data-ref-id");
                    const refIndex = parseInt(refId.replace("ref-", ""));

                    let previousNode = link.previousSibling;

                    while (
                        previousNode &&
                        previousNode.nodeType === Node.TEXT_NODE &&
                        !previousNode.textContent.trim()
                    ) {
                        previousNode = previousNode.previousSibling;
                    }

                    if (
                        previousNode &&
                        previousNode.classList &&
                        previousNode.classList.contains("reference-link")
                    ) {
                        return;
                    }

                    let targetElement = link.parentElement;

                    if (targetElement && refIndex >= 0 && refIndex < lookupReferences.length) {
                        if (!elementToReferenceMap.has(targetElement)) {
                            targetElement.setAttribute("data-ref-mapping", refIndex.toString());
                        }
                    }
                });

                referenceLinks.forEach((link) => {
                    let previousNode = link.previousSibling;
                    if (previousNode && previousNode.nodeName === "BR") {
                        previousNode.remove();
                    }
                    link.remove();
                });
            }

            const cleanedHtml = doc.body.innerHTML;

            const mappedElements = doc.querySelectorAll("[data-ref-mapping]");

            const allReferences = Array.from(mappedElements).map((element) => {
                const refIndex = parseInt(element.getAttribute("data-ref-mapping"));
                const ref = lookupReferences[refIndex];

                if (!ref?.fileId) {
                    return null;
                }

                const points = ref?.coordinates?.points;
                const width = ref?.coordinates?.layout_width;
                const height = ref?.coordinates?.layout_height;
                const coordinates = calculateAreaCoordinates(points, width, height);
                const area = JSON.stringify({ pageIndex: ref.page - 1, ...coordinates });

                if (ref.articleId) {
                    return {
                        type: "article",
                        articleId: ref.articleId,
                        stage: ref.stage,
                        articleType: ref.articleType,
                        area,
                    };
                }

                const document = chatStore.sourceDocuments.find(
                    (doc) => doc.getPdfFileId === ref.fileId,
                );
                const documentId = document?._id;

                return documentId ? { type: "source-document", documentId, area } : null;
            });

            if (allReferences.some(Boolean)) {
                htmlContent = assembleMixedCopyContent({
                    references: allReferences,
                    project,
                    content: cleanedHtml,
                });
            }
        }

        data["text/html"] = new Blob([htmlContent || content], { type: "text/html" });
        navigator.clipboard.write([new window.ClipboardItem(data)]);
    }

    return (
        <div className="answer box">
            <div className="header">
                <CopilotLogo size={40} />
                <div className="details">
                    <div className="user">CAPTIS Copilot</div>
                    <div className="time">{format.datetime(answer.created)}</div>
                </div>
                <div className="actions">
                    <Button
                        icon={answer.thumbsUp ? <LikeFilled /> : <LikeOutlined />}
                        type="icon"
                        onClick={onThumbsUp}
                    />
                    <Button
                        icon={answer.thumbsDown ? <DislikeFilled /> : <DislikeOutlined />}
                        type="icon"
                        onClick={onThumbsDown}
                    />
                    <Button icon={<CopyOutlined />} type="icon" onClick={copy} />
                </div>
            </div>
            <div
                ref={contentRef}
                className="content html"
                dangerouslySetInnerHTML={{ __html: content }}
            ></div>
            {answer.skipped?.length > 0 && <SkippedFiles skipped={answer.skipped} />}
        </div>
    );
});

const HistoryRecord = observer(
    ({
        chatId,
        record,
        allReferences,
        onShowRef,
        onThumbsUp,
        onThumbsDown,
        onCancel,
        onEditMessage,
    }) => {
        const isIndexing = record.status === "indexing";
        const isProcessing = record.status === "processing";
        const isError = record.status === "error";
        const isSuccess = record.status === "success";
        const isCancelled = record.status === "cancelled";
        const needsPolling = isIndexing || isProcessing;

        useEffect(() => {
            if (!needsPolling || !chatId) {
                return;
            }

            const pollInterval = setInterval(async () => {
                await chatStore.refreshMessages();
            }, POLL_INTERVAL);

            chatStore.refreshMessages();

            return () => {
                clearInterval(pollInterval);
            };
        }, [needsPolling, chatId]);

        const question = useMemo(() => {
            if (record.question) {
                return md.toHtml(record.question);
            }

            return undefined;
        }, [record.question]);

        const progressPercentage = record.progressPercentage;

        const getProcessingMessage = useCallback(() => {
            if (isIndexing) {
                return "Processing files...";
            }

            if (isProcessing) {
                return "Your request is currently being processed.";
            }

            return "";
        }, [isIndexing, isProcessing, progressPercentage]);

        const handleEdit = () => {
            if (onEditMessage && record.question) {
                onEditMessage(record.question);
            }
        };

        return (
            <div className="record">
                <div className="question">
                    <div className="header">
                        <Avatar user={record.user} size={40} />
                        <div className="details">
                            <div className="user">{record.user?.fullName}</div>
                            <div className="time">
                                {format.datetime(record.created)}
                                {record.context && record.context.length > 0 && (
                                    <Tooltip
                                        placement="bottom"
                                        title={
                                            <div>
                                                {record.context.map((ctx, idx) => (
                                                    <div key={idx}>
                                                        • {ctx.mdrId ? `C${ctx.mdrId} - ` : ""}
                                                        {ctx.name}
                                                        {ctx.type === "report document" &&
                                                        ctx.subType
                                                            ? ` (${
                                                                  ctx.subType === "sota"
                                                                      ? "SoTA"
                                                                      : "DUE"
                                                              })`
                                                            : ""}
                                                    </div>
                                                ))}
                                            </div>
                                        }
                                    >
                                        <span className="reference-header">
                                            {" "}
                                            •{" "}
                                            <b>
                                                <i>Context</i>
                                            </b>
                                        </span>
                                    </Tooltip>
                                )}
                            </div>
                        </div>
                        {onEditMessage && (
                            <div style={{ marginLeft: "auto" }}>
                                <Button
                                    type="icon"
                                    icon={<EditOutlined />}
                                    onClick={handleEdit}
                                    title="Edit prompt"
                                />
                            </div>
                        )}
                    </div>
                    <div className="text html" dangerouslySetInnerHTML={{ __html: question }}></div>
                </div>

                {(isIndexing || isProcessing) && (
                    <Operation progressPercentage={progressPercentage} onCancel={onCancel}>
                        {getProcessingMessage()}
                    </Operation>
                )}
                {isError && <Error message={record.error} />}
                {(isSuccess || isCancelled) && (
                    <Answer
                        answer={record.answer}
                        allReferences={allReferences}
                        onShowRef={onShowRef}
                        onThumbsUp={onThumbsUp}
                        onThumbsDown={onThumbsDown}
                    />
                )}
            </div>
        );
    },
);

export default History;
